import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function getAuthToken(getState) {
  const { loginToken, loginTokenCoUser } = getState();
  return loginToken?.response?.token || loginTokenCoUser?.response?.token;
}

function authHeaders(getState, { isFormData = false } = {}) {
  const token = getAuthToken(getState);
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
}

function businessIdFromState(getState) {
  return getState().loginToken?.response?.user?.business_id;
}

export const fetchWorkspaceSettings = createAsyncThunk(
  "workspaceSettings/fetch",
  async (_, { getState, rejectWithValue }) => {
    const businessId = businessIdFromState(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/business/get-business-detail/${businessId}`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const updateWorkspaceSettings = createAsyncThunk(
  "workspaceSettings/update",
  async (payload, { getState, rejectWithValue }) => {
    const businessId = businessIdFromState(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    if (!(payload instanceof FormData) && typeof payload !== "object") {
      return rejectWithValue({ message: "Invalid workspace update payload" });
    }
    try {
      if (payload instanceof FormData) {
        const response = await fetch(
          `${ROOT_URL}/business/onboard-business-update/${businessId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${getAuthToken(getState)}`,
            },
            body: payload,
          },
        );
        const data = await response.json();
        if (!response.ok) {
          return rejectWithValue(data || { message: "Update failed" });
        }
        return data;
      }

      const response = await axios.put(
        `${ROOT_URL}/business/onboard-business-update/${businessId}`,
        payload,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

const initialState = {
  data: null,
  loading: false,
  saving: false,
  error: null,
  statusLoaded: false,
};

const workspaceSettingsApi = createSlice({
  name: "workspaceSettings",
  initialState,
  reducers: {
    clearWorkspaceSettings: () => initialState,
    clearWorkspaceSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaceSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.statusLoaded = true;
        state.data = action.payload?.data ?? null;
      })
      .addCase(fetchWorkspaceSettings.rejected, (state, action) => {
        state.loading = false;
        state.statusLoaded = true;
        state.error = action.payload;
      })
      .addCase(updateWorkspaceSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateWorkspaceSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.data = action.payload?.data ?? state.data;
      })
      .addCase(updateWorkspaceSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearWorkspaceSettings, clearWorkspaceSettingsError } =
  workspaceSettingsApi.actions;

export default workspaceSettingsApi.reducer;
